import { useCallback, useMemo, useState } from "react";

import { useTrackEvent } from "../../../../libs/analytics";
import { getLinkedinTargetEntriesLabels } from "../../Post/PostDetails/linkedIn-details";
import type {
  AccountType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";
import type { Audiences, ListItem } from "./LinkedinTargetAudience";

/*
 * The one gating rule for LinkedIn audience targeting, shared by the single
 * platform form and the cluster form. It used to live only in the single form,
 * which is how the cluster form ended up with no way to set an audience at all
 * while still publishing the field. Both forms call this hook now, so the rule
 * cannot drift again.
 */

export const LINKEDIN_TARGETING_MULTIPLE_ACCOUNTS_TOOLTIP =
  "Audience targeting applies to one LinkedIn company page at a time. Select a single company page to set an audience.";

export const LINKEDIN_TARGETING_PERSONAL_PROFILE_TOOLTIP =
  "LinkedIn audience targeting is available on company pages, not personal profiles.";

export const LINKEDIN_TARGET_AUDIENCE_LISTS: ListItem[] = [
  {
    id: "jobFunctions",
    label: "Job function",
  },
  {
    id: "organizations",
    label: "University",
  },
  {
    id: "seniorities",
    label: "Seniority",
  },
  {
    id: "geoLocations",
    label: "Location",
  },
  {
    id: "staffCountRanges",
    label: "Company size",
  },
  {
    id: "industries",
    label: "Industry",
  },
];

export function getLinkedInAccountDetail(accounts: AccountType[]) {
  const linkedInAccounts = (accounts || []).filter(
    (profile) => profile.platformType === "LI"
  );
  const hasMoreThanOnelinkedInAccount = linkedInAccounts.length >= 2;
  let selectedLinkedInAccountType: PostLinkedInAccountType = "",
    selectedLinkedUrn = "";

  if (linkedInAccounts.length === 1) {
    const linkedInProfile = linkedInAccounts[0];

    if (linkedInProfile.linkedinUrn?.includes("person")) {
      selectedLinkedInAccountType = "personal";
    } else {
      selectedLinkedInAccountType = "company";
      selectedLinkedUrn =
        linkedInAccounts.length === 1 &&
        linkedInProfile.session &&
        linkedInProfile.session.scopes &&
        linkedInProfile.session.scopes.includes("r_organization_followers")
          ? linkedInProfile.linkedinUrn || ""
          : "";
    }
  }

  return {
    hasLinkedInAccount: linkedInAccounts.length > 0,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
  };
}

export type LinkedInTargeting = {
  /* No LinkedIn account selected, so there is nothing to target. */
  isVisible: boolean;
  isDisabled: boolean;
  /* Null when the control is usable, otherwise why it is not. */
  disabledTooltip: string | null;
  audience: Audiences;
  audienceLabels: string[];
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: (audience?: Audiences) => void;
  hasMoreThanOnelinkedInAccount: boolean;
  selectedLinkedInAccountType: PostLinkedInAccountType;
  selectedLinkedUrn: string;
};

export function useLinkedInTargeting({
  accounts,
  liTargetEntities,
  onAudienceChange,
}: {
  accounts: AccountType[];
  liTargetEntities: Audiences | undefined;
  onAudienceChange: (audience: Audiences) => void;
}): LinkedInTargeting {
  const trackEvent = useTrackEvent();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    hasLinkedInAccount,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
  } = useMemo(() => getLinkedInAccountDetail(accounts), [accounts]);

  const disabledTooltip = useMemo(() => {
    if (!hasLinkedInAccount) {
      return null;
    }

    if (hasMoreThanOnelinkedInAccount) {
      return LINKEDIN_TARGETING_MULTIPLE_ACCOUNTS_TOOLTIP;
    }

    if (selectedLinkedInAccountType === "personal") {
      return LINKEDIN_TARGETING_PERSONAL_PROFILE_TOOLTIP;
    }

    return null;
  }, [
    hasLinkedInAccount,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
  ]);

  /*
   * Read straight off the post rather than mirroring it into local state. The
   * post holds one liTargetEntities, so every place that renders this control
   * shows the same value and a write from either one is visible in both.
   */
  const audience = useMemo(() => liTargetEntities || {}, [liTargetEntities]);

  const audienceLabels = useMemo(
    () => getLinkedinTargetEntriesLabels(audience),
    [audience]
  );

  const openModal = useCallback(() => {
    trackEvent({
      eventName: "click-linkedin-targeting",
      useServices: ["AMP"],
    });
    setIsModalOpen(true);
  }, [trackEvent]);

  const closeModal = useCallback(
    (selected?: Audiences) => {
      if (selected) {
        onAudienceChange({ ...selected });
      }

      setIsModalOpen(false);
    },
    [onAudienceChange]
  );

  return {
    isVisible: hasLinkedInAccount,
    isDisabled: Boolean(disabledTooltip),
    disabledTooltip,
    audience,
    audienceLabels,
    isModalOpen,
    openModal,
    closeModal,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
  };
}
